import type React from 'react';
import { useMemo, useState, useEffect } from 'react';
import type { ServiceProviderResponse } from '@/types/service-provider.types';
import type { HarborKYCData } from '@/types/harbor-management.types';
import {
  useGetServiceProviderKYCQuery,
  useUpdateKYCStatusMutation,
} from '@/services/ServiceProviderService';
import {
  Briefcase,
  Building2,
  CalendarDays,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  UserCircle,
  X,
} from 'lucide-react';
import { Modal } from '@/components/modal';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/card/card';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
// import { LegalAcceptanceCard } from '@/components/legal-acceptance/LegalAcceptanceCard';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { ISubscription } from '@/types/subscription-management.types';
import { SubscriptionPlansCard } from '@/components';
import EntityCancelPlanButton from '@/components/subscription-management/EntityCancelPlanButton';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserRoles, KYCStatus } from '@/constant';
import { selectServiceProviderCapabilities } from '@/store/serviceProviderCapabilitiesSlice';
import { ServiceProviderCapability } from '@/constant/ServiceProvider';
import { useGetCommissionPolicyByActorQuery } from '@/services/CommissionPolicyService';
import { ActorType, CommercialMode } from '@/types/commission-policy.types';
import {
  formatFinnishCoordinate,
  formatFinnishPercentage,
  formatInternationalPhoneNumber,
} from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

interface ServiceProviderDetailsTabProps {
  serviceProvider: ServiceProviderResponse;
}

export const ServiceProviderDetailsTab: React.FC<
  ServiceProviderDetailsTabProps
> = ({ serviceProvider }) => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const isAdmin = userData?.roles?.includes(UserRoles.ADMIN);

  const canReadKyc = hasPermission('kyc_management_read');
  const canReadSubscriptions = hasPermission('subscription_management_read');
  const canAccessSubscriptions = canReadSubscriptions || !isAdmin;

  const canReadCommissionPolicy = hasPermission(
    'commission_policy_management_read'
  );

  const actorId = serviceProvider?.id ?? null;
  const { data: effectiveCommissionPolicy } =
    useGetCommissionPolicyByActorQuery(
      {
        actor_id: actorId,
        actor_type: ActorType.SERVICE_PROVIDER,
      },
      { skip: !actorId || !canReadCommissionPolicy }
    );

  const commissionSummary = useMemo(() => {
    if (!effectiveCommissionPolicy) return 'Not set';
    if (
      effectiveCommissionPolicy.commercial_mode ===
      CommercialMode.SUBSCRIPTION_ONLY
    ) {
      return `Subscription only (${formatFinnishPercentage(0)})`;
    }
    const modeDisplay = effectiveCommissionPolicy.commercial_mode.replace(
      /_/g,
      ' '
    );
    return `${formatFinnishPercentage(
      effectiveCommissionPolicy.commission_rate
    )} (${modeDisplay})`;
  }, [effectiveCommissionPolicy]);

  // Get service provider capabilities from Redux (fetched when clicking service provider name)
  const serviceProviderCapabilities = useSelector(
    selectServiceProviderCapabilities
  );

  // Check if DISCOVERY_MANAGE_BASIC_PROFILE capability is present
  const hasManageBasicProfileCapability = useMemo(() => {
    if (
      !serviceProviderCapabilities ||
      serviceProviderCapabilities.length === 0
    ) {
      return false;
    }
    return serviceProviderCapabilities.includes(
      ServiceProviderCapability.DISCOVERY_MANAGE_BASIC_PROFILE
    );
  }, [serviceProviderCapabilities]);

  const statusLabel = useMemo(() => {
    if (!serviceProvider) return 'Unknown';
    switch (serviceProvider.status) {
      case 0:
        return 'Pending';
      case 1:
        return 'Active';
      case 2:
        return 'Archived';
      case 3:
        return 'Suspended';
      case 4:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }, [serviceProvider?.status]);

  const statusColorClass = useMemo(() => {
    if (!serviceProvider) {
      return 'bg-gray-500/10 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 contrast:bg-gray-900 contrast:text-gray-100 contrast:border-gray-600';
    }
    switch (serviceProvider.status) {
      case 0:
        // Pending
        return 'bg-blue-500/10 text-blue-700 border-blue-300 dark:bg-blue-900 dark:text-blue-100 dark:border-blue-700 contrast:bg-blue-900 contrast:text-blue-100 contrast:border-blue-600';
      case 1:
        // Active
        return 'bg-green-500/10 text-green-700 border-green-300 dark:bg-green-900 dark:text-green-100 dark:border-green-700 contrast:bg-green-900 contrast:text-green-100 contrast:border-green-600';
      case 2:
        // Archived
        return 'bg-gray-500/10 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 contrast:bg-gray-900 contrast:text-gray-100 contrast:border-gray-600';
      case 3:
        // Suspended
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-100 dark:border-yellow-700 contrast:bg-yellow-900 contrast:text-yellow-100 contrast:border-yellow-600';
      case 4:
        // Rejected
        return 'bg-red-500/10 text-red-700 border-red-300 dark:bg-red-900 dark:text-red-100 dark:border-red-700 contrast:bg-red-900 contrast:text-red-100 contrast:border-red-600';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-300 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 contrast:bg-gray-900 contrast:text-gray-100 contrast:border-gray-600';
    }
  }, [serviceProvider?.status]);

  const handleEditServiceProvider = () => {
    navigate(`/service-providers/edit/${serviceProvider.id}`);
  };

  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [planCancellationRequested, setPlanCancellationRequested] =
    useState(false);
  const [planSubscriptionEndDate, setPlanSubscriptionEndDate] = useState<
    string | null
  >(null);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  // KYC Update Status Modal state
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<
    | KYCStatus.PENDING
    | KYCStatus.UNDER_REVIEW
    | KYCStatus.APPROVED
    | KYCStatus.REJECTED
    | KYCStatus.REQUIRES_UPDATE
    | KYCStatus.NOT_REQUIRED
    | KYCStatus.ON_HOLD
  >(KYCStatus.PENDING);
  const [kycNotes, setKycNotes] = useState<string>('');
  const [kycNotesError, setKycNotesError] = useState<string>('');

  // Fetch KYC data
  const {
    data: kycDataResponse,
    isLoading: kycLoading,
    error: kycError,
    refetch: refetchKYC,
  } = useGetServiceProviderKYCQuery(serviceProvider?.id || '', {
    skip: !serviceProvider?.id || !canReadKyc,
  });

  // Handle both cases: data wrapped in ApiResponse or returned directly
  const kycDataRaw = useMemo((): HarborKYCData | null => {
    if (!kycDataResponse) return null;
    // If it has a data property (ApiResponse wrapper)
    if ('data' in kycDataResponse && kycDataResponse.data) {
      return kycDataResponse.data as HarborKYCData;
    }
    // If it's the data directly (same format as API response)
    // Check if it has KYC data properties (like id, legal_name, etc.)
    if ('id' in kycDataResponse && 'legal_name' in kycDataResponse) {
      return kycDataResponse as unknown as HarborKYCData;
    }
    return null;
  }, [kycDataResponse]);

  // API returns plaintext account fields
  const kycData = kycDataRaw;

  // Update KYC Status mutation
  const [updateKYCStatus, { isLoading: isUpdatingKYC }] =
    useUpdateKYCStatusMutation();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setSubscriptionsLoading(true);

        const response = await SubscriptionsService.getSubscriptionsByType(
          'service-provider',
          serviceProvider?.id ? { serviceProviderId: serviceProvider.id } : {}
        );
        setSubscriptions(response.subscriptions || []);

        // Fetch current plan for this service provider
        if (serviceProvider?.id) {
          const currentPlan =
            await SubscriptionsService.getCurrentServiceProviderPlan(
              serviceProvider.id
            );
          setCurrentPlanId(currentPlan?.subscription?.id || null);
          setPlanCancellationRequested(
            !!currentPlan?.cancellation_requested_at
          );
          setPlanSubscriptionEndDate(
            currentPlan?.subscription_end_date ?? null
          );
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
        setSubscriptions([]);
        setCurrentPlanId(null);
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    if (serviceProvider?.id && canAccessSubscriptions) {
      fetchSubscriptions();
    } else if (!canAccessSubscriptions) {
      setSubscriptions([]);
      setSubscriptionsLoading(false);
      setCurrentPlanId(null);
    }
  }, [serviceProvider?.id, canAccessSubscriptions, subscriptionRefreshKey]);

  // Capabilities are now fetched when clicking service provider name/view icon
  // They are stored in Redux and will be available here via selectServiceProviderCapabilities
  // No need to fetch capabilities here anymore

  const handleUpgrade = (planId: string) => {
    const selectedPlan = subscriptions.find((s) => s.id === planId);
    if (!selectedPlan || !serviceProvider?.id) return;

    navigate('/subscription/payment', {
      state: {
        subscriptionId: planId,
        subscriptionName: selectedPlan.name,
        subscriptionPrice: selectedPlan.price,
        entityType: 'service-provider',
        entityId: serviceProvider.id,
        returnUrl: `/service-providers/details`,
      },
    });
  };

  const currentSubscription =
    subscriptions.find((s) => s.id === currentPlanId) || null;

  if (!serviceProvider) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No service provider data available.</p>
      </div>
    );
  }

  return (
    <div className="service-provider-page service-provider-form container mx-auto px-4">
      {/* --- Header --- */}
      <header className="service-provider-view-header relative mb-8 overflow-hidden rounded-2xl bg-slate-800 p-8 text-white shadow-lg">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('/placeholder.svg?width=1200&height=400')`,
          }}
        />
        <div className="relative z-10 flex flex-col items-start gap-6 md:flex-row md:items-center">
          {serviceProvider.image && (
            <img
              src={
                typeof serviceProvider.image === 'string'
                  ? serviceProvider.image
                  : undefined
              }
              alt={`${serviceProvider.display_name} Logo`}
              className="service-provider-logo h-24 w-24 rounded-full border-4 border-white bg-white object-contain shadow-2xl"
            />
          )}
          <div className="flex-grow">
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              {serviceProvider.display_name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${statusColorClass}`}
              >
                {statusLabel}
              </div>
              {serviceProvider.country_short_code && (
                <div className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  {serviceProvider.country_short_code}
                </div>
              )}
            </div>
          </div>
          {hasPermission('service_provider_management_update') &&
            hasManageBasicProfileCapability && (
              <button
                className="bg-primary p-2 text-primary-foreground hover:bg-primary/80 inline-flex items-start rounded-full border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={handleEditServiceProvider}
                aria-label="Edit Service Provider"
              >
                <Pencil className="h-6 w-6" />
              </button>
            )}
        </div>
      </header>

      {/* --- Main Content - Flex Layout --- */}
      <div className="flex flex-col lg:flex-row gap-8 mb-8">
        {/* --- Left/Main Column --- */}
        <div className="space-y-8 flex-1">
          <Card>
            <CardHeader>
              <CardTitle>About {serviceProvider.display_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {serviceProvider.description || 'No description provided.'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary contrast:text-white" />{' '}
                Services Offered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {serviceProvider.services?.length ? (
                  Array.from(
                    new Map(
                      serviceProvider.services.map((service) => [service.id, service]),
                    ).values(),
                  ).map((service) => (
                    <div
                      className="text-foreground inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-gray-500"
                      key={service.id}
                    >
                      {service.name}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No services listed.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary contrast:text-white" />{' '}
                Linked Harbors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {serviceProvider.linked_harbors?.length ? (
                  Array.from(
                    new Map(
                      serviceProvider.linked_harbors.map((harbor) => [harbor.harbor_id, harbor]),
                    ).values(),
                  ).map((harbor) => (
                    <div
                      key={harbor.harbor_id}
                      className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg border border-gray-200 w-full"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {harbor.harbor?.name || 'Unknown Harbor'}
                        </h3>
                        <p className="text-xs text-gray-600 mt-1">
                          Linked Harbor
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No harbors linked.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* --- Right/Sidebar Column --- */}
        <div className="space-y-8 lg:w-96 flex-shrink-0">
          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <DetailItem
                icon={UserCircle}
                label="Legal Name"
                value={serviceProvider.legal_name}
              />
              <DetailItem
                icon={Briefcase}
                label="VAT ID"
                value={serviceProvider.vat_id}
              />
              <DetailItem
                icon={Globe}
                label="Website"
                value={serviceProvider.website}
                isLink
              />
            </CardContent>
          </Card>

          {/* <LegalAcceptanceCard
            summary={serviceProvider.legal_acceptance_summary}
            className=""
          /> */}

          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-6 pb-6">
              <DetailItem
                icon={Phone}
                label="Phone"
                value={formatInternationalPhoneNumber(
                  serviceProvider.contact_phone,
                  serviceProvider.country_code,
                  serviceProvider.country_short_code
                )}
              />
              <DetailItem
                icon={Mail}
                label="Email"
                value={serviceProvider.contact_email}
                isLink={`mailto:${serviceProvider.contact_email}`}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Location and System Information - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Location</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-6 pb-6">
            <DetailItem
              icon={MapPin}
              label="Street"
              value={serviceProvider.street}
            />
            <DetailItem
              icon={MapPin}
              label="City"
              value={serviceProvider.city}
            />
            <DetailItem
              icon={MapPin}
              label="Post Code"
              value={serviceProvider.post_code}
            />
            <DetailItem
              icon={Globe}
              label="Country"
              value={serviceProvider.country}
            />
            <DetailItem
              icon={Globe}
              label="Coordinates"
              value={
                serviceProvider.latitude && serviceProvider.longitude
                  ? `${formatFinnishCoordinate(
                      serviceProvider.latitude
                    )}, ${formatFinnishCoordinate(serviceProvider.longitude)}`
                  : undefined
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-6 pb-6">
            <DetailItem
              icon={CalendarDays}
              label="Created On"
              value={
                serviceProvider.created_at &&
                formatDisplayDate(new Date(serviceProvider.created_at))
              }
            />
            <DetailItem
              icon={CalendarDays}
              label="Last Updated"
              value={
                serviceProvider.updated_at &&
                formatDisplayDate(new Date(serviceProvider.updated_at))
              }
            />
          </CardContent>
        </Card>
      </div>

      {/* KYC Details Card */}
      {canReadKyc && (
        <div className="mt-8">
          <div className="card w-full max-w-full mx-auto">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  KYC Details
                </h3>
                {hasPermission('kyc_management_change_status') && (
                  <button
                    type="button"
                    onClick={() => {
                      setKycStatus(
                        (kycData?.kyc_status as
                          | KYCStatus.PENDING
                          | KYCStatus.UNDER_REVIEW
                          | KYCStatus.APPROVED
                          | KYCStatus.REJECTED
                          | KYCStatus.REQUIRES_UPDATE
                          | KYCStatus.NOT_REQUIRED
                          | KYCStatus.ON_HOLD) || KYCStatus.PENDING
                      );
                      setKycNotes(kycData?.admin_notes || '');
                      setKycNotesError('');
                      setIsUpdateStatusModalOpen(true);
                    }}
                    disabled={!kycData || kycLoading}
                    className="btn btn-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update Status
                  </button>
                )}
              </div>

              {kycLoading ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">Loading KYC data...</p>
                </div>
              ) : kycError ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-red-500">
                    Failed to load KYC data. Please try again later.
                  </p>
                </div>
              ) : !kycData ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">
                    No KYC data available.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {kycData.legal_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Legal Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.legal_name}
                        </span>
                      </div>
                    )}
                    {kycData.business_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Business ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.business_id}
                        </span>
                      </div>
                    )}
                    {kycData.vat_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          VAT ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.vat_id}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_1 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 1
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_1}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_2 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 2
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_2}
                        </span>
                      </div>
                    )}
                    {kycData.postal_code && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Postal Code
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.postal_code}
                        </span>
                      </div>
                    )}
                    {kycData.city && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          City
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.city}
                        </span>
                      </div>
                    )}
                    {kycData.state && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          State
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.state}
                        </span>
                      </div>
                    )}
                    {kycData.country && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Country
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.country}
                        </span>
                      </div>
                    )}
                    {kycData.account_holder_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Holder Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_holder_name}
                        </span>
                      </div>
                    )}
                    {kycData.account_number && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Number
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_number}
                        </span>
                      </div>
                    )}
                    {kycData.iban && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          IBAN
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all md:break-normal">
                          {kycData.iban}
                        </span>
                      </div>
                    )}
                    {kycData.bic && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          BIC
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono">
                          {kycData.bic}
                        </span>
                      </div>
                    )}
                    {kycData.bank_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Bank Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.bank_name}
                        </span>
                      </div>
                    )}
                    {kycData.kyc_status && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          KYC Status
                        </span>
                        <span className="mt-1">
                          <span
                            className={`text-sm font-semibold capitalize ${
                              (kycData.kyc_status as string) ===
                              KYCStatus.APPROVED
                                ? 'text-green-600'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.REJECTED
                                  ? 'text-red-600'
                                  : (kycData.kyc_status as string) ===
                                        KYCStatus.UNDER_REVIEW ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.NOT_REQUIRED ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.ON_HOLD
                                    ? 'text-yellow-600'
                                    : 'text-gray-600'
                            }`}
                          >
                            {(kycData.kyc_status as string) ===
                            KYCStatus.PENDING
                              ? 'Pending'
                              : (kycData.kyc_status as string) ===
                                  KYCStatus.UNDER_REVIEW
                                ? 'Under Review'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.APPROVED
                                  ? 'Approved'
                                  : (kycData.kyc_status as string) ===
                                      KYCStatus.REJECTED
                                    ? 'Rejected'
                                    : (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE
                                      ? 'Requires Update'
                                      : (kycData.kyc_status as string) ===
                                          KYCStatus.NOT_REQUIRED
                                        ? 'Not Required'
                                        : (kycData.kyc_status as string) ===
                                            KYCStatus.ON_HOLD
                                          ? 'On Hold'
                                          : (kycData.kyc_status as string) ===
                                              KYCStatus.RESUBMITTED
                                            ? 'Resubmitted'
                                            : kycData.kyc_status}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Admin Notes */}
                  {kycData.admin_notes && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-1">
                        Admin Notes
                      </h4>
                      <p className="text-sm text-gray-900 whitespace-pre-line">
                        {kycData.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KYC Update Status Modal */}
      <Modal
        open={isUpdateStatusModalOpen}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setKycNotesError('');
        }}
        className="!flex items-center justify-center"
        aria-labelledby="update-kyc-status-title"
      >
        <div className="bg-white dark:bg-gray-800 contrast:bg-[#101010] p-6 w-full max-w-md mx-auto rounded-lg shadow-xl">
          <div className="flex justify-between items-center mb-4">
            <h2 id="update-kyc-status-title" className="text-xl font-bold">
              Update KYC Status
            </h2>
            <button
              onClick={() => {
                setIsUpdateStatusModalOpen(false);
                setKycNotesError('');
              }}
              className="p-1 hover:bg-gray-100 rounded"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="kyc-status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Status
              </label>
              <select
                id="kyc-status"
                value={kycStatus}
                onChange={(e) =>
                  setKycStatus(
                    e.target.value as
                      | KYCStatus.PENDING
                      | KYCStatus.UNDER_REVIEW
                      | KYCStatus.APPROVED
                      | KYCStatus.REJECTED
                      | KYCStatus.REQUIRES_UPDATE
                      | KYCStatus.NOT_REQUIRED
                      | KYCStatus.ON_HOLD
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white contrast:bg-[#101010] contrast:text-white"
              >
                <option value={KYCStatus.PENDING}>Pending</option>
                <option value={KYCStatus.UNDER_REVIEW}>Under Review</option>
                <option value={KYCStatus.APPROVED}>Approved</option>
                <option value={KYCStatus.REJECTED}>Rejected</option>
                <option value={KYCStatus.NOT_REQUIRED}>Not Required</option>
                <option value={KYCStatus.REQUIRES_UPDATE}>
                  Requires Update
                </option>
                <option value={KYCStatus.ON_HOLD}>On Hold</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="kyc-notes"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                id="kyc-notes"
                value={kycNotes}
                onChange={(e) => {
                  setKycNotes(e.target.value);
                  if (e.target.value.trim()) {
                    setKycNotesError('');
                  }
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 resize-none dark:bg-gray-700 text-gray-900 dark:text-white ${
                  kycNotesError
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'
                }`}
                placeholder="Enter admin notes..."
              />
              {kycNotesError && (
                <p className="mt-1 text-xs text-red-500">{kycNotesError}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => {
                setIsUpdateStatusModalOpen(false);
                setKycNotesError('');
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!kycData?.id) {
                  console.error('KYC ID not available');
                  return;
                }

                if (!kycNotes.trim()) {
                  setKycNotesError('Notes are required');
                  return;
                }

                try {
                  await updateKYCStatus({
                    kycId: kycData.id,
                    entityId: serviceProvider?.id || '',
                    data: {
                      kyc_status: kycStatus,
                      admin_notes: kycNotes,
                    },
                  }).unwrap();

                  // Refetch KYC data after successful update
                  await refetchKYC();

                  setIsUpdateStatusModalOpen(false);
                  // You can add a success notification here
                } catch (error) {
                  console.error('Failed to update KYC status:', error);
                  // You can add an error notification here
                }
              }}
              disabled={isUpdatingKYC || !kycData?.id}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdatingKYC ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </Modal>

      {canReadCommissionPolicy && (
        <Card className="mt-3">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Commercial Terms</span>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() =>
                  navigate(
                    `/service-providers/${serviceProvider.id}/commission`,
                    {
                      state: { from: `${location.pathname}${location.search}` },
                    }
                  )
                }
              >
                Commission Policy - Manage
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 text-sm text-muted-foreground">
            Current commission: {commissionSummary}
          </CardContent>
        </Card>
      )}

      {/* Subscription Plans Section - Full Width */}
      {canAccessSubscriptions && isAdmin && (
        <Card className="mt-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary contrast:text-white" />{' '}
              Service Provider Subscription Plans
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {serviceProvider?.id && (
              <EntityCancelPlanButton
                entityType="service-provider"
                entityId={serviceProvider.id}
                entityName={serviceProvider.display_name}
                planPrice={currentSubscription?.price}
                cancellationRequested={planCancellationRequested}
                subscriptionEndDate={planSubscriptionEndDate}
                onSuccess={() =>
                  setSubscriptionRefreshKey((key) => key + 1)
                }
                className="mb-4"
              />
            )}
            {subscriptionsLoading ? (
              <SubscriptionPlansCard
                subscriptions={currentSubscription ? [currentSubscription] : []}
                loading={subscriptionsLoading}
                currentPlanId={currentPlanId}
              />
            ) : currentSubscription ? (
              <SubscriptionPlansCard
                subscriptions={[currentSubscription]}
                loading={false}
                currentPlanId={currentPlanId}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No current plan found for this service provider.
              </p>
            )}
          </CardContent>
        </Card>
      )}
      {canAccessSubscriptions && !isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" /> Service Provider
              Subscription Plans
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <SubscriptionPlansCard
              subscriptions={subscriptions}
              loading={subscriptionsLoading}
              currentPlanId={currentPlanId}
              onUpgrade={handleUpgrade}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Helper component for detail items
const DetailItem: React.FC<{
  icon: React.ElementType;
  label: string;
  value?: string | number | null;
  isLink?: boolean | string;
}> = ({ icon: Icon, label, value, isLink = false }) => {
  if (!value) return null;

  const content =
    isLink && typeof value === 'string' ? (
      <a
        href={typeof isLink === 'string' ? isLink : value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline contrast:text-blue-200"
      >
        {value}
      </a>
    ) : (
      <span className="text-muted-foreground">{value}</span>
    );

  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <div className="flex flex-col">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <div className="text-sm break-all">{content}</div>
      </div>
    </div>
  );
};
